import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CalendarIcon, AlertTriangle, ArrowLeft, FileText } from 'lucide-react';
import { useQueryState } from 'nuqs';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector';
import { PartySelector } from '@/app/components/common/PartySelector';
import { OutstandingInvoicesTable } from './components/OutstandingInvoicesTable';
import { paymentInWithAllocationsSchema, advancePaymentSchema, PaymentInFormValues, formatPaymentInPayload } from './schema';
import { useAccountFilters } from '@/app/hooks/useAccountFilters';
import { recordPaymentIn, listOutstandingInvoices } from '@/app/services/paymentService';
import { listParties } from '@/app/services/partyService';
import { getCheque } from '@/app/services/chequeService';
import { OutstandingInvoice } from '@/app/types/payment';
import { Checkbox } from '@/components/ui/checkbox';
import { ChequeRecord } from '@/app/types/cheque';
import { parseISO } from 'date-fns';

const PaymentsInPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPartyId, setSelectedPartyId] = useState<number | null>(null);
  const [isAdvancePayment, setIsAdvancePayment] = useState(false);
  const [chequeData, setChequeData] = useState<ChequeRecord | null>(null);
  
  // Get account filters based on user's default site
  const { paymentAccountIds } = useAccountFilters();
  
  // Use nuqs for URL state management
  const [partyIdFromUrl] = useQueryState('party_id', {
    parse: (value) => value ? parseInt(value, 10) : null,
  });

  const [chequeIdFromUrl] = useQueryState('cheque_id', {
    parse: (value) => value ? parseInt(value, 10) : null,
  });

  const form = useForm<PaymentInFormValues>({
    resolver: zodResolver(isAdvancePayment ? advancePaymentSchema : paymentInWithAllocationsSchema),
    defaultValues: {
      party_id: undefined,
      received_in_account_id: undefined,
      total_payment_amount: 0,
      transaction_date: new Date(),
      reference_number: '',
      narration: '',
      allocations: [], // Will be populated based on selected party's invoices
      source_cheque_record_id: null,
    },
    mode: 'onChange', // Validate on change for immediate feedback
  });

  const totalPaymentAmount = form.watch('total_payment_amount') || 0;

  // --- Queries ---
  // Fetch parties for the selector but don't need to track data/loading state
  // as PartySelector component handles its own loading state
  useQuery({
    queryKey: ['parties', { limit: 100 }], // Fetch many parties for selector
    queryFn: () => listParties({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: outstandingInvoices = [],
    isLoading: isLoadingInvoices,
    isError: isErrorInvoices,
    error: errorInvoices,
  } = useQuery<OutstandingInvoice[], Error>({
    queryKey: ['outstandingInvoices', selectedPartyId],
    queryFn: () => {
      if (!selectedPartyId) return Promise.resolve([]) as Promise<OutstandingInvoice[]>; // Don't fetch if no party selected
      return listOutstandingInvoices(selectedPartyId) as Promise<OutstandingInvoice[]>;
    },
    enabled: !!selectedPartyId, // Only run query when a party is selected
    staleTime: 1 * 60 * 1000, // 1 minute stale time
  });

  // --- Mutations ---
  const recordPaymentMutation = useMutation({
    mutationFn: recordPaymentIn,
    onSuccess: (data) => {
      toast.success(`Payment ${data.voucher_number} recorded successfully!`);
      queryClient.invalidateQueries({ queryKey: ['vouchers'] }); // Invalidate generic vouchers
      queryClient.invalidateQueries({ queryKey: ['paymentsIn'] }); // Invalidate payments list
      queryClient.invalidateQueries({ queryKey: ['parties', selectedPartyId] }); // Invalidate party details? (for balance)
      queryClient.invalidateQueries({ queryKey: ['outstandingInvoices', selectedPartyId] }); // Re-fetch invoices
      // Optionally reset form or navigate away
      // form.reset();
      // setSelectedPartyId(null);
      navigate('/payments-in/list'); // Navigate back to payments list page
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      const errorMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to record payment: ${errorMsg}`);
      console.error("Payment recording failed:", error);
    },
  });

  // --- Event Handlers ---
  const handlePartyChange = (partyId: number | undefined): void => {
    const id = partyId ? Number(partyId) : null;
    setSelectedPartyId(id);
    // Reset allocations when party changes
    form.setValue('allocations', []);
    if (id) {
        // Trigger refetch immediately after selection
        // refetchInvoices is not directly available, use queryClient invalidation
        queryClient.invalidateQueries({ queryKey: ['outstandingInvoices', id] });
    }
  };

  const onSubmit = (data: PaymentInFormValues): void => {
      const payload = formatPaymentInPayload(data);

      // For advance payments, clear allocations
      if (isAdvancePayment) {
          payload.allocations = [];
      } else {
          // Final check before submitting for regular payments
          const totalAllocated = payload.allocations.reduce((sum, alloc) => sum + alloc.amount_to_allocate, 0);
          if (totalAllocated > payload.total_payment_amount + 0.001) {
              toast.error("Total allocated amount cannot exceed the Payment Amount.");
              return;
          }
          if (payload.total_payment_amount > 0 && payload.allocations.length === 0) {
              toast.error("Please select at least one invoice to allocate the payment to.");
              return;
          }
      }

      console.log('Submitting Payment Data:', payload);
      recordPaymentMutation.mutate(payload);
  };


  // --- Effects ---
   // Set party from URL parameter on component mount
   useEffect(() => {
       if (partyIdFromUrl && !form.getValues('party_id')) {
           form.setValue('party_id', partyIdFromUrl);
           setSelectedPartyId(partyIdFromUrl);
           handlePartyChange(partyIdFromUrl);
       }
   }, [partyIdFromUrl]);

   // Load cheque data if cheque_id is provided
   useEffect(() => {
       if (chequeIdFromUrl) {
           getCheque(chequeIdFromUrl)
               .then((cheque) => {
                   setChequeData(cheque);
                   // Pre-populate form with cheque data
                   form.setValue('party_id', cheque.party_id);
                   form.setValue('total_payment_amount', cheque.amount);
                   form.setValue('reference_number', cheque.cheque_number);
                   form.setValue('narration', `Clearing cheque ${cheque.cheque_number}`);
                   form.setValue('source_cheque_record_id', cheque.id);
                   // Try to parse the clearing date
                   try {
                       form.setValue('transaction_date', parseISO(cheque.clearing_date));
                   } catch {
                       // If parsing fails, use current date
                       form.setValue('transaction_date', new Date());
                   }
                   setSelectedPartyId(cheque.party_id);
                   handlePartyChange(cheque.party_id);
               })
               .catch((error) => {
                   toast.error(`Failed to load cheque: ${error.message}`);
               });
       }
   }, [chequeIdFromUrl]);


   // Refetch invoices when selectedPartyId changes (alternative to manual refetch call)
   useEffect(() => {
       if (selectedPartyId) {
           queryClient.prefetchQuery({
               queryKey: ['outstandingInvoices', selectedPartyId],
               queryFn: () => listOutstandingInvoices(selectedPartyId),
           });
       }
   }, [selectedPartyId, queryClient]);

   // Update form validation when advance payment checkbox changes
   useEffect(() => {
       // Clear allocations when switching to advance payment
       if (isAdvancePayment) {
           form.setValue('allocations', []);
       }
       // Trigger validation to update form state
       form.trigger();
   }, [isAdvancePayment, form]);


  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold">
                  {chequeData ? 'Clear Cheque - Record Payment In' : 'Record Payment In'}
              </h1>
          </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Cheque Alert Banner */}
          {chequeData && (
              <Alert className="border-orange-200 bg-orange-50">
                  <FileText className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-900">
                      <strong>Clearing Cheque #{chequeData.cheque_number}</strong>
                      <br />
                      Party: {chequeData.party_name || chequeData.party?.name || 'N/A'} |
                      Amount: ₹{chequeData.amount.toLocaleString('en-IN')} |
                      Bank: {chequeData.bank_name || 'N/A'}
                      <br />
                      <span className="text-sm text-muted-foreground">
                          This payment will automatically clear the cheque record upon successful submission.
                      </span>
                  </AlertDescription>
              </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
               {/* Top Row: Party, Account, Amount, Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                     {/* Customer Selection */}
                    <FormField
                        control={form.control}
                        name="party_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Customer *</FormLabel>
                            <PartySelector
                                value={field.value}
                                onChange={(value) => {
                                    field.onChange(value); // Update form state
                                    handlePartyChange(value); // Trigger side effects
                                }}
                                placeholder="Select Customer"
                                disabled={recordPaymentMutation.isPending || !!chequeData}
                            />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    {/* Received In Account */}
                    <FormField
                        control={form.control}
                        name="received_in_account_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Received In Account *</FormLabel>
                            {/* Filter to show only cash and bank accounts */}
                            <SimpleAccountSelector
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select Cash/Bank Account"
                                filterAccountIds={paymentAccountIds}
                                disabled={recordPaymentMutation.isPending}
                            />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    {/* Payment Amount */}
                    <FormField
                        control={form.control}
                        name="total_payment_amount"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Payment Amount *</FormLabel>
                            <FormControl>
                                <Input
                                    type="number"
                                    step="0.01"
                                    {...field}
                                    onChange={e => field.onChange(parseFloat(e.target.value || '0'))}
                                    value={field.value?.toString() ?? ''}
                                    placeholder="0.00"
                                    disabled={recordPaymentMutation.isPending}
                                    className="text-right"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                     {/* Transaction Date */}
                    <FormField
                        control={form.control}
                        name="transaction_date"
                        render={({ field }) => (
                        <FormItem className="flex flex-col pt-1.5"> {/* Align label better */}
                            <FormLabel>Payment Date *</FormLabel>
                            <Popover>
                            <PopoverTrigger asChild>
                                <FormControl>
                                <Button
                                    variant="outline"
                                    className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                    )}
                                    disabled={recordPaymentMutation.isPending}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                                </Button>
                                </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                initialFocus
                                />
                            </PopoverContent>
                            </Popover>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>

                {/* Second Row: Reference, Narration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {/* Reference Number */}
                    <FormField
                        control={form.control}
                        name="reference_number"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Reference #</FormLabel>
                            <FormControl>
                            <Input
                                {...field}
                                placeholder="e.g., Cheque No, Txn ID"
                                value={field.value ?? ''}
                                disabled={recordPaymentMutation.isPending}
                            />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    {/* Narration */}
                    <FormField
                        control={form.control}
                        name="narration"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                            <Textarea
                                {...field}
                                placeholder="Optional notes about the payment"
                                value={field.value ?? ''}
                                rows={1}
                                disabled={recordPaymentMutation.isPending}
                            />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>

                {/* Advance Payment Checkbox */}
                <div className="flex items-center space-x-2 mt-4">
                  <Checkbox
                    id="advance-payment"
                    checked={isAdvancePayment}
                    onCheckedChange={(checked) => setIsAdvancePayment(checked as boolean)}
                    disabled={recordPaymentMutation.isPending}
                  />
                  <Label
                    htmlFor="advance-payment"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    This is an advance payment (no invoice allocation)
                  </Label>
                </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Invoice Allocation Section */}
          {selectedPartyId && !isAdvancePayment && (
              <Card>
                <CardContent className="pt-6">
                    {isErrorInvoices && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Error loading outstanding invoices: {(errorInvoices as Error)?.message}
                          </AlertDescription>
                        </Alert>
                    )}
                    <OutstandingInvoicesTable
                        form={form}
                        invoices={outstandingInvoices}
                        isLoading={isLoadingInvoices}
                        totalPaymentAmount={totalPaymentAmount}
                    />
                </CardContent>
              </Card>
          )}


          {/* Submit Button */}
          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={recordPaymentMutation.isPending || !form.formState.isValid}>
              {recordPaymentMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Recording...
                </>
              ) : (
                'Record Payment'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PaymentsInPage; 